import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import { CaseScreen, caseViewQuery } from '../features/case-view'
import { type CaseActionId, isCaseActionId } from '../features/case-view/actions/registry'
import { ApiError, type CaseView } from '../features/case-view/api'
import { CaseNotFound, MergedNotice } from '../features/case-view/not-found'

/** `action` opens the case at one of its safe action surfaces; GET itself remains side-effect free. */
export type CaseAction = CaseActionId
type CaseSearch = { dispatch?: boolean; merged_from?: string; action?: CaseAction; focus?: string; notification?: string }

export const Route = createFileRoute('/case/$id')({
  validateSearch: (search: Record<string, unknown>): CaseSearch => ({
    ...(search.dispatch === true || search.dispatch === 'true' ? { dispatch: true } : {}),
    ...(typeof search.merged_from === 'string' && /^[\w-]{1,64}$/.test(search.merged_from) ? { merged_from: search.merged_from } : {}),
    ...(isCaseActionId(search.action) ? { action: search.action } : {}),
    ...(typeof search.focus === 'string' && /^(turn|card|flag|blocker|next_move):[^\s]{1,200}$/.test(search.focus)
      ? { focus: search.focus }
      : {}),
    ...(typeof search.notification === 'string' && /^[0-9a-f-]{36}$/i.test(search.notification)
      ? { notification: search.notification }
      : {}),
  }),
  // Header and threads come from the mirror at once; rail sections load on their own. A merged case redirects to the
  // case that survived (the API follows the merge chain); a case that does not exist gets a page with a search box.
  loader: async ({ context, params }) => {
    let view: CaseView
    try {
      view = await context.queryClient.ensureQueryData(caseViewQuery(params.id))
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) throw notFound()
      throw e
    }
    if (view.mergedInto && view.mergedInto !== view.id) {
      throw redirect({ to: '/case/$id', params: { id: view.mergedInto }, search: { merged_from: view.id }, replace: true })
    }
    return null
  },
  notFoundComponent: function CaseMissing() {
    const { id } = Route.useParams()
    return <CaseNotFound reference={id} />
  },
  component: function CaseRoute() {
    const { id } = Route.useParams()
    const { dispatch, merged_from: mergedFrom, action, focus } = Route.useSearch()
    return (
      <CaseScreen
        key={id}
        id={id}
        dispatch={Boolean(dispatch)}
        action={action}
        focus={focus}
        notice={mergedFrom ? <MergedNotice from={mergedFrom} into={id} /> : undefined}
      />
    )
  },
})
